import { faChevronRight } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  JoonUIColor,
  SPACING,
  Switch,
  TextButton,
  Typography,
} from "@joonapp/web-shared"
import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import {
  RestrictionTimeRangeModal,
  useRestrictionTimeRangeModalStore,
} from "./RestrictionTimeRangeModal"
import { useScreenTimeRestrictionsConfirmationModalStore } from "./ScreenTimeRestrictionsConfirmationModal"
import { CloseButton } from "../../../components/closeButton/CloseButton"
import { SelectRow } from "../../../components/selectRow/SelectRow"
import { SelectRowContainer } from "../../../components/selectRowContainer/SelectRowContainer"
import SlideUpModal from "../../../components/slideUpModal/SlideUpModal"
import { DAY_INDEX_MAP, REPETITIONS } from "../../../constants"
import {
  useCreateScreenTimeScheduledExceptionMutation,
  useDeleteScreenTimeScheduledExceptionMutation,
  useUpdateScreenTimeScheduledExceptionMutation,
} from "../../../networking/rewards/mutations"
import { useScreenTimeScheduledExceptionsQuery } from "../../../networking/rewards/queries"
import { ScreenTimeRestrictionsConfirmationModalType } from "../../../types/rewards"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../../util/analytics"
import { convertTo24HourTime, convertTo12HourTime } from "../../../util/util"
import useRewardsUserSelectStore from "../hooks/useRewardsUserSelectStore"

interface ScreenTimeRestrictionsModalStore {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
  exceptionDays: boolean[]
  setExceptionDays: (days: boolean[]) => void
  exceptionTimeMapping: Record<string, { start: string; end: string }>
  setExceptionTimeMapping: (day: string, start: string, end: string) => void
}

export const useScreenTimeRestrictionsModalStore =
  create<ScreenTimeRestrictionsModalStore>((set, get) => ({
    isOpen: false,
    onOpen: () => set({ isOpen: true }),
    onClose: () =>
      set({
        isOpen: false,
        exceptionDays: REPETITIONS.noDays,
        exceptionTimeMapping: {},
      }),
    exceptionDays: REPETITIONS.noDays,
    setExceptionDays: (days: boolean[]) => set({ exceptionDays: days }),
    exceptionTimeMapping: {},
    setExceptionTimeMapping: (day: string, start: string, end: string) => {
      const { exceptionTimeMapping } = get()
      if (!start && !end) {
        const newExceptionTimeMapping = {
          ...exceptionTimeMapping,
        }
        delete newExceptionTimeMapping[day]
        set({
          exceptionTimeMapping: newExceptionTimeMapping,
        })
        return
      }
      set({
        exceptionTimeMapping: {
          ...exceptionTimeMapping,
          [day]: { start, end },
        },
      })
    },
  }))

// Get days array from DAY_INDEX_MAP keys, sorted by index
const DAYS_OF_WEEK = Object.keys(DAY_INDEX_MAP).sort(
  (a, b) =>
    DAY_INDEX_MAP[a as keyof typeof DAY_INDEX_MAP] -
    DAY_INDEX_MAP[b as keyof typeof DAY_INDEX_MAP]
) as (keyof typeof DAY_INDEX_MAP)[]

const DAY_FULL_KEYS = {
  monday: "DAY_FULL_MONDAY",
  tuesday: "DAY_FULL_TUESDAY",
  wednesday: "DAY_FULL_WEDNESDAY",
  thursday: "DAY_FULL_THURSDAY",
  friday: "DAY_FULL_FRIDAY",
  saturday: "DAY_FULL_SATURDAY",
  sunday: "DAY_FULL_SUNDAY",
} as const

const ScreenTimeRestrictionsModal = () => {
  const { selectedUserId } = useRewardsUserSelectStore()
  const { t } = useTranslation()
  const {
    isOpen,
    onClose,
    exceptionDays,
    setExceptionDays,
    exceptionTimeMapping,
    setExceptionTimeMapping,
  } = useScreenTimeRestrictionsModalStore()
  const { onOpen: onOpenScreenTimeRestrictionsConfirmationModal } =
    useScreenTimeRestrictionsConfirmationModalStore()
  const { onOpen: onOpenRestrictionTimeRangeModal } =
    useRestrictionTimeRangeModalStore()
  const { data: scheduledExceptions } = useScreenTimeScheduledExceptionsQuery({
    userId: selectedUserId,
  })
  const createScreenTimeScheduledExceptionMutation =
    useCreateScreenTimeScheduledExceptionMutation()
  const updateScreenTimeScheduledExceptionMutation =
    useUpdateScreenTimeScheduledExceptionMutation()
  const deleteScreenTimeScheduledExceptionMutation =
    useDeleteScreenTimeScheduledExceptionMutation()
  const isEditing = !!scheduledExceptions?.length

  const noChangesMade = useMemo(() => {
    if (!scheduledExceptions) return false
    const hasSameDays = exceptionDays.every((day, index) => {
      const existingException = scheduledExceptions.find(
        (exception) => exception.weekday === DAYS_OF_WEEK[index]
      )
      return day === !!existingException
    })
    const hasSameTimes = Object.entries(exceptionTimeMapping).every(
      ([day, { start, end }]) => {
        const existingException = scheduledExceptions.find(
          (exception) => exception.weekday === day
        )
        return (
          convertTo12HourTime(start) === existingException?.start_time &&
          convertTo12HourTime(end) === existingException?.end_time
        )
      }
    )
    return hasSameDays && hasSameTimes
  }, [exceptionDays, exceptionTimeMapping, scheduledExceptions])

  useEffect(() => {
    if (isOpen && scheduledExceptions && scheduledExceptions.length > 0) {
      const newExceptionDays: boolean[] = []
      DAYS_OF_WEEK.forEach((day) => {
        newExceptionDays.push(
          scheduledExceptions.some((exception) => exception.weekday === day)
        )
      })
      scheduledExceptions.forEach((exception) => {
        setExceptionTimeMapping(
          exception.weekday,
          convertTo24HourTime(exception.start_time),
          convertTo24HourTime(exception.end_time)
        )
      })
      setExceptionDays([...newExceptionDays])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const onSwitchDay = (index: number) => {
    const isTurningOff = exceptionDays[index]
    setExceptionDays(exceptionDays.map((day, i) => (i === index ? !day : day)))
    if (isTurningOff) {
      setExceptionTimeMapping(DAYS_OF_WEEK[index], "", "")
      return
    }
    onOpenRestrictionTimeRangeModal(DAYS_OF_WEEK[index])
  }

  const getTimeRangeText = (day: string) => {
    const start = exceptionTimeMapping[day]?.start || ""
    const end = exceptionTimeMapping[day]?.end || ""
    return start && end
      ? `${convertTo12HourTime(start)} - ${convertTo12HourTime(end)}`
      : t("STRM_SELECT_TIME") // should never happen
  }

  const onSave = async () => {
    if (!selectedUserId) return
    trackAnalyticEvent(ANALYTIC_EVENTS.SCREENTIME_SAVE_SCHEDULED_EXCEPTION)
    await Promise.all(
      DAYS_OF_WEEK.map((day, index) => {
        const existingException = scheduledExceptions?.find(
          (exception) => exception.weekday === day
        )
        if (!exceptionDays[index] && existingException) {
          return deleteScreenTimeScheduledExceptionMutation.mutateAsync(
            existingException.id
          )
        }
        if (exceptionDays[index] && !existingException) {
          return createScreenTimeScheduledExceptionMutation.mutateAsync({
            userId: selectedUserId,
            weekday: DAYS_OF_WEEK[index],
            start_time: convertTo12HourTime(exceptionTimeMapping[day]?.start),
            end_time: convertTo12HourTime(exceptionTimeMapping[day]?.end),
          })
        }
        if (exceptionDays[index] && existingException) {
          return updateScreenTimeScheduledExceptionMutation.mutateAsync({
            exceptionId: existingException.id,
            start_time: convertTo12HourTime(exceptionTimeMapping[day]?.start),
            end_time: convertTo12HourTime(exceptionTimeMapping[day]?.end),
          })
        }
        return Promise.resolve()
      })
    )
    onClose()
    onOpenScreenTimeRestrictionsConfirmationModal(
      ScreenTimeRestrictionsConfirmationModalType.SCHEDULE
    )
  }

  return (
    <SlideUpModal
      isOpen={isOpen}
      onClose={onClose}
      backgroundType="overlay"
      size="fit-content"
    >
      <div className="w-full h-full flex flex-col align-start gap-4 p-4 bg-bgXLightGray">
        <CloseButton onClick={onClose} />
        <Typography variant="bodyBold">
          {isEditing ? t("STRM_EDIT_EXCEPTION") : t("STRM_SCHEDULE_EXCEPTION")}
        </Typography>
        <Typography variant="bodySmall">{t("STRM_TURN_OFF_ON")}</Typography>
        <SelectRowContainer
          style={{ backgroundColor: JoonUIColor.background.primaryNeutral }}
        >
          {DAYS_OF_WEEK.map((day, index) => (
            <SelectRow
              key={index}
              hideBorder
              showBorderBottom={index !== DAYS_OF_WEEK.length - 1}
            >
              <div className="flex flex-row items-center justify-start gap-2 w-fit">
                <Switch
                  checked={exceptionDays[index]}
                  onChange={() => onSwitchDay(index)}
                  name={day}
                  variant="small"
                />
                <Typography variant="bodySmall">
                  {t(DAY_FULL_KEYS[day])}
                </Typography>
              </div>
              {exceptionDays[index] && (
                <TextButton
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "end",
                    gap: SPACING.space2,
                    paddingTop: SPACING.space05,
                    paddingBottom: SPACING.space05,
                    width: "fit-content",
                  }}
                  onClick={() => onOpenRestrictionTimeRangeModal(day)}
                >
                  <Typography variant="bodySmall">
                    {getTimeRangeText(day)}
                  </Typography>
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    color={JoonUIColor.icon.neutral}
                    fontSize={12}
                  />
                </TextButton>
              )}
            </SelectRow>
          ))}
        </SelectRowContainer>
        {(!isEditing || (isEditing && !noChangesMade)) && (
          <Button
            onClick={onSave}
            text={t("QUEST_SAVE")}
            fullWidth
            disabled={!isEditing && exceptionDays.every((day) => !day)}
            isLoading={
              createScreenTimeScheduledExceptionMutation.isLoading ||
              updateScreenTimeScheduledExceptionMutation.isLoading ||
              deleteScreenTimeScheduledExceptionMutation.isLoading
            }
          />
        )}
      </div>
      <RestrictionTimeRangeModal />
    </SlideUpModal>
  )
}

export default ScreenTimeRestrictionsModal
